import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
} from '@angular/core';
import { Router } from '@angular/router';
import {
  IpoLanguageService,
  IpoTranslatePipe,
} from '@ip-only/front-end-library';
import { Language } from '../../../../shared/language-chooser/language-chooser-entities';
import { DeliveryReportPdfService } from '../../../../shared/pdf-tools/pdf-report/delivery-report-pdf.service';
import {
  RP_CABINET_FIELD_REPORT_OPENED,
  RP_WELL_FIELD_REPORT_OPENED,
} from '../../../../shared/posthug-analytics/analytics-event-types';
import { AnalyticsService } from '../../../../shared/posthug-analytics/analytics.service';
import { RolloutProgressReportEntities } from '../../../../shared/self-inspection-reports/service/entities';
import { ToastsService } from '../../../../shared/toast/toast.service';
import {
  DistributionPointProperties,
  PointType,
} from '../../../span-progress-entities';
import {
  cancelPdfDownloadOnPointChange,
  getIfsFromUrl,
  pointIdIdentityChanged,
  POPUP_PDF_DOWNLOAD_TOOLTIP,
  openReportInNewTab,
} from '../popup-pdf.utils';
import {
  createPopupPdfContext,
  PopupPdfDownloadController,
} from '../popup-pdf-download.controller';

@Component({
  selector: 'app-distribution-point-popup-content',
  template: `
    <app-reportable-popup-shell
      [paragraphHeadingText]="
        (properties.telematorId || '') + ' ' + '(' + properties.name + ')'
      "
      [status]="status"
      [isLoading]="pdfController.isPdfDownloadInProgress"
      [downloadTooltipLabel]="downloadTooltipLabel"
      [primaryButtonText]="'report.open'"
      [secondaryButtonText]="'close'"
      [incomingDucts]="properties.incomingDucts"
      [outgoingDucts]="properties.outgoingDucts"
      (trigger)="onDownloadPreviewPdf($event)"
      (triggerKeydown)="onDownloadPreviewPdfKeyDown($event)"
      (primaryButtonClick)="openV3ReportPage()"
      (secondaryButtonClick)="onClosePopup()"
    />
  `,
  standalone: false,
})
export class DistributionPopupContentComponent implements OnChanges, OnDestroy {
  private readonly analyticsService = inject(AnalyticsService);
  private readonly router = inject(Router);
  private readonly pdfService = inject(DeliveryReportPdfService);
  private readonly languageService = inject(IpoLanguageService);
  private readonly toastsService = inject(ToastsService);
  private readonly translate = inject(IpoTranslatePipe);

  PointType = PointType;

  constructor() {
    this.pdfController = new PopupPdfDownloadController(
      this.pdfService,
      this.toastsService,
      this.translate,
      createPopupPdfContext(
        () => {
          const ifsNumber = getIfsFromUrl(this.router.url);
          const deliveryOrderId = this.properties.id;
          if (!ifsNumber || !deliveryOrderId) return null;
          return {
            ifsNumber,
            deliveryOrderId,
            locale:
              (this.languageService.getStoredLanguage() as Language) ?? 'en',
            address: this.properties.name ?? '-',
            rolloutPointType: this.getRolloutMarkerType(),
          };
        },
        () => this.destroyed,
      ),
    );
  }

  @Input() status: string;
  @Input() properties: DistributionPointProperties;
  @Output() dismiss = new EventEmitter();
  downloadTooltipLabel = POPUP_PDF_DOWNLOAD_TOOLTIP;
  pdfController: PopupPdfDownloadController;

  private destroyed = false;

  private getRolloutMarkerType(): RolloutProgressReportEntities.v3.MarkerTypes {
    return this.properties.markerTypeId === PointType.Cabinet
      ? RolloutProgressReportEntities.v3.MarkerTypes.CABINET
      : RolloutProgressReportEntities.v3.MarkerTypes.MANHOLE;
  }

  analyticsButtonClicked() {
    const { markerTypeId } = this.properties;
    switch (markerTypeId) {
      case PointType.Cabinet:
        this.analyticsService.logEvent(RP_CABINET_FIELD_REPORT_OPENED);
        break;
      case PointType.CableWell:
        this.analyticsService.logEvent(RP_WELL_FIELD_REPORT_OPENED);
        break;
      case PointType.SlingWell:
        this.analyticsService.logEvent(RP_WELL_FIELD_REPORT_OPENED);
        break;
    }
  }

  onClosePopup() {
    this.dismiss.emit();
  }

  openV3ReportPage = (): void => {
    this.analyticsButtonClicked();
    openReportInNewTab(
      this.router.url,
      this.getRolloutMarkerType(),
      this.properties.id,
    );
  };

  ngOnChanges(changes?: SimpleChanges): void {
    cancelPdfDownloadOnPointChange(changes, pointIdIdentityChanged, () =>
      this.pdfController.cleanup(),
    );
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.pdfController.cleanup();
  }

  onDownloadPreviewPdf(event: MouseEvent): void {
    this.pdfController.onDownloadPreviewPdf(event);
  }

  onDownloadPreviewPdfKeyDown(event: KeyboardEvent): void {
    this.pdfController.onDownloadPreviewPdfKeyDown(event);
  }
}
